import {Injectable} from '@angular/core';
import {AuthService, ConfigService} from 'shared';
import {webSocket} from 'rxjs/webSocket';
import {WebsocketEvent} from './websocket-event';
import {WebSocketSubject} from 'rxjs/internal/observable/dom/WebSocketSubject';
import {catchError, delayWhen, filter, finalize, map, retryWhen, skip, tap} from 'rxjs/operators';
import {Observable, Subscription, timer} from 'rxjs';
import {decode, encode} from '@msgpack/msgpack';
import {UserDomain} from '../../user-domains/user-domain';
import {UserDomainService} from '../../user-domains/user-domain.service';

@Injectable({
  providedIn: 'root'
})
export class WebsocketService {

  private socket: WebSocketSubject<WebsocketEvent>;
  public messages: Observable<WebsocketEvent>;

  constructor(
    private config: ConfigService,
    private auth: AuthService,
    private userDomainsService: UserDomainService,
  ) {

    let endpoint = `${this.config.get('wsEndpoint')}?token=${auth.getAccessToken()}`;

    this.socket = webSocket<WebsocketEvent>({
      url: endpoint,
      openObserver: {
        next: () => {
          console.log('[Socket Service]: connection ok');

          this.selectDomain(this.userDomainsService.selectedDomain());
        }
      },
      closeObserver: {
        next: () => {
          console.log('[Socket Service]: connection closed');
          endpoint = '';
        }
      },
      serializer: (value: WebsocketEvent) => {
        return encode(value);
      },
      deserializer: (e: MessageEvent) => {
        return decode(e.data) as WebsocketEvent;
      },
      binaryType: 'arraybuffer',
    });

    this.messages = this.socket.pipe(

      catchError(e => {
        throw e;
      }),

      // Reconnect
      retryWhen(
        errors => errors.pipe(
          // log error message
          tap(_ => console.log('[Socket Service] Try to reconnect')),
          // retry
          delayWhen(_ => timer(this.config.get('wsReconnectInterval', 2000))),
        )
      )
    );

    // Connect
    this.messages.subscribe();

    this.userDomainsService.domainChanged.pipe(skip(1)).subscribe((domain?: UserDomain) => {
      this.selectDomain(domain);
    });
  }

  send(event: WebsocketEvent) {
    this.socket.next(event);
  }

  topicObserver<T>(topic: string, model: new(data: T) => T): Observable<T> {
    return this.messages.pipe(
      filter(e => e.topic === topic),
      map(e => {
        return new model(e.payload);
      })
    );
  }

  private selectDomain(userDomain?: UserDomain) {
    console.log('[Socket Service] Select domain');
    this.send({topic: 'SelectDomain', payload: userDomain ? userDomain.id : 0});
  }
}
